import { TTableCellTree, TTableInfo, TTableTreeModel } from '../table.type';

export const TASK_SELECT_TABLE = {
  addFeatureSelect: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    if (tableModel.options?.isViewOnly) {
      return tableModel;
    }
    const selectOptions = tableModel.options?.select;
    if (!selectOptions) return tableModel;
    const selectCell: TTableCellTree = {
      type: 'SELECT_CELL',
      location: selectOptions.position ? 'HEAD' : 'TAIL',
      isChecked: false,
      isIntermediate: false,
      isMulti: selectOptions.isMulti,

      isVisible: true,
      widthInPx: selectOptions.size === 'DEFAULT' ? 36 : 50,
      ...(selectOptions &&
        selectOptions.stickyType && {
          stickyType: selectOptions.stickyType,
          stickyPosition: 0,
        }),
    };

    const addHeaderCells: TTableInfo<T>['header'] = {
      ...tableModel.tableInfo.header,
      cells:
        selectOptions.position === 'START'
          ? [selectCell, ...tableModel.tableInfo.header.cells]
          : [...tableModel.tableInfo.header.cells, selectCell],
    };

    const addedCell = tableModel.tableInfo.rows.map((v) => ({
      ...v,
      cells:
        selectOptions.position === 'START'
          ? [{ ...selectCell, isDisabled: v.rowSelectionInfo?.isDisabled }, ...v.cells]
          : [...v.cells, { ...selectCell, isDisabled: v.rowSelectionInfo?.isDisabled }],
    }));

    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        header: addHeaderCells,
        rows: addedCell,
      },
    };
  },
  updateRowSelectCell: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const updatedRows = rows.map((row) => {
      const cells = row.cells.map((cell) => {
        return {
          ...cell,
          ...(cell.type === 'SELECT_CELL' && {
            isChecked: row.rowSelectionInfo?.isSelected ? row.rowSelectionInfo.isSelected : false,
          }),
        };
      });
      return {
        ...row,
        cells: [...cells],
      };
    });
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updatedRows,
      },
    };
  },
  updateHeaderSelectCell: <T>(tableTreeModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { header, rows } = tableTreeModel.tableInfo;
    const selectedCells = rows.filter((v) =>
      v.cells.some((cell) => cell.type === 'SELECT_CELL' && cell.isChecked === true),
    );
    const enabledRows = rows.filter((v) => v.cells.some((cell) => cell.type === 'SELECT_CELL' && !cell.isDisabled));

    const isIntermediate = selectedCells.length > 0 && selectedCells.length < enabledRows.length;
    const isChecked = selectedCells.length === enabledRows.length && enabledRows.length > 0;

    return {
      ...tableTreeModel,
      tableInfo: {
        ...tableTreeModel.tableInfo,
        header: {
          ...header,
          cells: header.cells.map((v) => {
            if (v.type === 'SELECT_CELL') {
              return {
                ...v,
                isChecked,
                isIntermediate,
              };
            }
            return v;
          }),
        },
      },
    };
  },
  enableAll: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const updatedRows = rows.map((row) => {
      return {
        ...row,
        rowSelectionInfo: {
          isDisabled: false,
          isSelected: !!row.rowSelectionInfo?.isSelected,
        },
      };
    });
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updatedRows,
        header: {
          ...tableModel.tableInfo.header,
          rowSelectionInfo: {
            isDisabled: false,
            isSelected: !!tableModel.tableInfo.header.rowSelectionInfo?.isSelected,
          },
        },
      },
    };
  },
  unselectAll: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const updatedRows = rows.map((row) => {
      return {
        ...row,
        rowSelectionInfo: {
          isDisabled: !!row.rowSelectionInfo?.isDisabled,
          isSelected: false,
        },
      };
    });
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updatedRows,
      },
    };
  },
  disableAll: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const updatedRows = rows.map((row) => {
      return {
        ...row,
        rowSelectionInfo: {
          isDisabled: true,
          isSelected: !!row.rowSelectionInfo?.isSelected,
        },
      };
    });
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        header: {
          ...tableModel.tableInfo.header,
          rowSelectionInfo: {
            isDisabled: true,
            isSelected: !!tableModel.tableInfo.header.rowSelectionInfo?.isSelected,
          },
        },
        rows: updatedRows,
      },
    };
  },
  hideSingleSelectHeader: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        header: {
          ...tableModel.tableInfo.header,
          cells: tableModel.tableInfo.header.cells.map((v) => {
            if (v.type === 'SELECT_CELL' && !v.isMulti) {
              return {
                ...v,
                isVisible: false,
              };
            }
            return v;
          }),
        },
      },
    };
  },
};
